<template>
    <BasePanel
        headline="lang_CONTRACTS_PUBLISHING_CAPTION_AGREEMENT_NUMBERS"
        :alvqa="alvqa"
        :onOpenPanel="onOpenPanel"
    >
        <template v-slot:panelContent>
            <v-data-table
                :headers="headers"
                :items="listItems"
                hide-default-footer
                :items-per-page="300"
                :header-props="{ sortIcon: '' }"
                :no-data-text="$t('lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')"
                class="noMinWidth"
            >
                <template v-slot:top>
                    <v-row no-gutters class="pb-6">
                        <v-col cols="3">
                            <v-select
                                v-model="sourceFilter"
                                :items="sourceFilterOptions"
                                :label="$t('lang_GENERIC_CAPTION_SOURCE')"
                                hide-details
                                :alvqa="alvqa + '-dropdown-source-filter'"
                            ></v-select>
                        </v-col>
                    </v-row>
                </template>

                <template v-for="(header, i) in headers" v-slot:[`header.${header.value}`]>
                    <span :alvqa="header.alvqa" :key="i">{{ header.text }}</span>
                </template>

                <template v-slot:[`item.sourceAlvId`]="{ item }">
                    <div :alvqa="alvqa + '-list-row-' + item.id + '-text-source-alv-id'">
                        {{ getSourceName(item.sourceAlvId) }}
                    </div>
                </template>
                <template v-slot:[`item.subcatalogId`]="{ item }">
                    <div :alvqa="alvqa + '-list-row-' + item.id + '-text-catalogue'">
                        {{ getCatalogueName(item.subcatalogId) }}
                    </div>
                </template>
                <template v-slot:[`item.agreementNumber`]="{ item }">
                    <div :alvqa="alvqa + '-list-row-' + item.id + '-text-agreement-number'">
                        {{ item.agreementNumber }}
                    </div>
                </template>
            </v-data-table>
        </template>
    </BasePanel>
</template>

<script>
import BasePanel from '@/components/_base/BasePanel'

export default {
    name: "PublishingContractsAgreementNumbersReadOnlyPanel",
    components: {
        BasePanel
    },
    props: {
        contractAlvId: {
            type: String
        }
    },
    data() {
        return {
            alvqa: 'publishing-contracts-list-row-' + this.contractAlvId + '-read-only-panel-agreement-numbers',
            sourceFilter: 'all'
        }
    },
    computed: {
        headers() {
            return [
                {
                    text: this.$t("lang_GENERIC_CAPTION_SOURCE"),
                    align: "left",
                    value: "sourceAlvId",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-source'
                },
                {
                    text: this.$t("lang_GENERIC_CAPTION_CATALOGUE"),
                    align: "left",
                    value: "subcatalogId",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-catalogue'
                },
                {
                    text: this.$t("lang_CONTRACTS_PUBLISHING_CAPTION_AGREEMENT_NUMBER"),
                    align: "left",
                    value: "agreementNumber",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-agreement-number'
                }
            ]
        },
        listItems() {
            const rows = this.$store.getters.getPublishingContractsAgreementNumbers || []
            return rows
                .filter(row => this.sourceFilter === 'all' || row.sourceAlvId === this.sourceFilter)
                .slice()
                .sort((a, b) => {
                    const bySource = this.getSourceName(a.sourceAlvId).localeCompare(this.getSourceName(b.sourceAlvId))
                    if (bySource !== 0) return bySource
                    return this.getCatalogueName(a.subcatalogId).localeCompare(this.getCatalogueName(b.subcatalogId))
                })
        },
        // only societies the account is a direct member of, same list as the CWR export recipient
        sourceOptions() {
            const societies = this.$store.getters.getAzSocietiesWhereDirectMember || []
            return societies.map(society => ({ value: society.alvId, text: society.societyAcronym }))
        },
        sourceFilterOptions() {
            return [{ text: this.$t('lang_GENERIC_CAPTION_ALL'), value: 'all' }].concat(this.sourceOptions)
        },
        catalogueOptions() {
            const subCatalogueCaption = this.$t('lang_PUBLISHING_WORKS_CAPTION_SUBCATALOGUE')
            const mainName = this.getLicensorName(this.$store.getters.getPublishingContractsDetailsLicensor)
            const mainCaption = this.$t('lang_CONTRACTS_PUBLISHING_CAPTION_MAIN_CATALOGUE')

            const options = [{ text: mainName ? mainCaption + ': ' + mainName : mainCaption, value: null }]
            const subCatalogues = this.$store.getters.getPublishingContractsSubCatalogues || []
            subCatalogues.forEach(subCatalogue => {
                options.push({
                    text: subCatalogueCaption + ': ' + this.getLicensorName(subCatalogue.licensor),
                    value: subCatalogue.id
                })
            })
            return options
        }
    },
    methods: {
        getLicensorName(licensor) {
            if (!licensor) return ''
            if (licensor.group !== null && licensor.group !== undefined && licensor.group !== '') return licensor.group
            return licensor.gemaName || licensor.name || ''
        },
        getCatalogueName(subcatalogId) {
            const found = this.catalogueOptions.find(option => option.value === (subcatalogId || null))
            return found ? found.text : ''
        },
        getSourceName(alvId) {
            if (!alvId) return ''
            const found = this.sourceOptions.find(source => source.value === alvId)
            if (found) return found.text + " (" + alvId + ")"
            // membership id of that society was removed in A-Z afterwards, the entry itself stays valid
            return this.$t('lang_GENERIC_CAPTION_NOT_A_MEMBER') + " (" + alvId + ")"
        },
        async onOpenPanel() {
            await this.$store.dispatch("fetchAzSocietiesWhereDirectMember")
            // catalogue names come from the subcatalogues, which are not loaded outside the edit page
            this.$store.dispatch('fetchPublishingContractsSubCatalogues')
        }
    }
}
</script>

<style>
    .noMinWidth table {
        min-width: 0px !important;
    }
</style>
